import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isRestrictedVendor } from '../utils/permissions';
import { downloadCsv } from '../utils/csv';
import { vendorService } from '../services/dataService';
import styles from './assets/css/VendorsPage.module.css';

const initialCreateForm = {
  vendor_alpha_code: '',
  dha_vendor_code: '',
  sha_vendor_code: '',
  name: '',
  description: '',
  address: '',
  email: '',
  phone: '',
  website: '',
  is_vendor: true,
  modality_ids: [],
};

const initialEditForm = {
  vendor_alpha_code: '',
  name: '',
  description: '',
  address: '',
  country: '',
  email: '',
  phone: '',
  website: '',
  is_vendor: true,
  lifecycle_state: 'active',
  modality_ids: [],
};

const VendorsPage = () => {
  const { user } = useAuth();
  const isVendorUser = isRestrictedVendor(user);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalities, setModalities] = useState([]);

  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState(initialEditForm);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const loadVendors = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const data = await vendorService.getAll({
        page: targetPage,
        page_size: pageSize,
        lifecycle_state: lifecycle || undefined,
        search: search || undefined,
      });
      setVendors(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.detail || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, lifecycle, search]);

  const loadModalities = useCallback(async () => {
    try {
      const data = await vendorService.getModalities({ status: 'active' });
      setModalities(data.items || []);
    } catch (_) {
      setModalities([]);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    loadModalities();
  }, [loadModalities]);

  const getSelectedValues = (event) => Array.from(event.target.selectedOptions, (option) => option.value);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadVendors(1);
  };

  const exportVendors = async () => {
    setExporting(true);
    setError('');
    try {
      const allItems = await vendorService.getAllPages({
        lifecycle_state: lifecycle || undefined,
        search: search || undefined,
      });
      downloadCsv(
        `vendors-${new Date().toISOString().slice(0, 10)}.csv`,
        allItems,
        [
          [(row, index) => index + 1, '#'],
          ['name', 'Name'],
          ['vendor_alpha_code', 'Vendor Code'],
          ['dha_vendor_code', 'DHA Vendor Code'],
          ['sha_vendor_code', 'SHA Vendor Code'],
          ['email', 'Email'],
          ['phone', 'Phone'],
          ['website', 'Website'],
          ['country', 'Country'],
          ['address', 'Address'],
          [(row) => (row.is_vendor ? 'Yes' : 'No'), 'Is Vendor'],
          [(row) => (row.modalities || []).map((modality) => modality.code).join('; '), 'Modalities'],
          [(row) => (row.contacts || []).length, 'Contacts'],
          ['lifecycle_state', 'State'],
          ['created_at', 'Created At'],
          ['updated_at', 'Updated At'],
        ],
      );
    } catch (err) {
      setError(err?.detail || 'Failed to export vendors');
    } finally {
      setExporting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await vendorService.create(createForm);
      setCreateForm(initialCreateForm);
      setShowCreateModal(false);
      await loadVendors();
    } catch (err) {
      setError(err?.detail || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (vendor) => {
    setEditId(vendor.id);
    setEditForm({
      name: vendor.name || '',
      vendor_alpha_code: vendor.vendor_alpha_code || '',
      description: vendor.description || '',
      address: vendor.address || '',
      country: vendor.country || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      website: vendor.website || '',
      is_vendor: vendor.is_vendor ?? true,
      lifecycle_state: vendor.lifecycle_state || 'active',
      modality_ids: (vendor.modalities || []).map((modality) => modality.id),
    });
  };

  const cancelEdit = () => {
    setEditId('');
    setEditForm(initialEditForm);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    setError('');
    try {
      await vendorService.update(editId, editForm);
      cancelEdit();
      await loadVendors();
    } catch (err) {
      setError(err?.detail || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>{isVendorUser ? 'Vendor Profile' : 'Vendor Management'}</h1>
        {!isVendorUser ? (
          <button className={styles.addButton} type="button" onClick={() => setShowCreateModal(true)}>Add Vendor</button>
        ) : null}
      </div>

      {!isVendorUser ? (
        <div className={styles.panel}>
          <h2>Filters</h2>
          <form onSubmit={onSearch} className={styles.grid2}>
            <input
              placeholder="Search by name, vendor code, DHA code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}>
              <option value="">All lifecycle states</option>
              <option value="active">active</option>
              <option value="disabled">disabled</option>
              <option value="retired">retired</option>
            </select>
            <button type="submit">Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={exportVendors} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </form>
        </div>
      ) : null}

      {showCreateModal ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Create Vendor</h2>
              <button type="button" className={styles.secondaryButton} onClick={() => setShowCreateModal(false)}>Close</button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleCreate} className={styles.grid3}>
              <input required placeholder="Vendor code (e.g AAA01)" value={createForm.vendor_alpha_code} onChange={(e) => setCreateForm((v) => ({ ...v, vendor_alpha_code: e.target.value.toUpperCase() }))} />
              <input required placeholder="DHA vendor code" value={createForm.dha_vendor_code} onChange={(e) => setCreateForm((v) => ({ ...v, dha_vendor_code: e.target.value }))} />
              <input required placeholder="SHA vendor code" value={createForm.sha_vendor_code} onChange={(e) => setCreateForm((v) => ({ ...v, sha_vendor_code: e.target.value }))} />
              <input required placeholder="Vendor name" value={createForm.name} onChange={(e) => setCreateForm((v) => ({ ...v, name: e.target.value }))} />
              <input type="email" placeholder="Email" value={createForm.email} onChange={(e) => setCreateForm((v) => ({ ...v, email: e.target.value }))} />
              <input placeholder="Phone" value={createForm.phone} onChange={(e) => setCreateForm((v) => ({ ...v, phone: e.target.value }))} />
              <input placeholder="Website" value={createForm.website} onChange={(e) => setCreateForm((v) => ({ ...v, website: e.target.value }))} />
              <input placeholder="Address" value={createForm.address} onChange={(e) => setCreateForm((v) => ({ ...v, address: e.target.value }))} />
              <label className={styles.checkboxField}>
                <input type="checkbox" checked={createForm.is_vendor} onChange={(e) => setCreateForm((v) => ({ ...v, is_vendor: e.target.checked }))} />
                <span>Is vendor</span>
              </label>
              <select
                multiple
                className={styles.multiSelect}
                value={createForm.modality_ids}
                onChange={(e) => setCreateForm((v) => ({ ...v, modality_ids: getSelectedValues(e) }))}
              >
                {modalities.map((modality) => (
                  <option key={modality.id} value={modality.id}>
                    {modality.code} - {modality.name}
                  </option>
                ))}
              </select>
              <textarea className={styles.colSpan3} placeholder="Description" value={createForm.description} onChange={(e) => setCreateForm((v) => ({ ...v, description: e.target.value }))} />
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Vendor'}</button>
                <button type="button" className={styles.secondaryButton} onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {!showCreateModal && !editId && error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.panel}>
        <h2>Vendor List</h2>
        {loading ? <p>Loading vendors...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Vendor code</th>
                <th>Email</th>
                <th>Modalities</th>
                <th>State</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>{v.vendor_alpha_code || '-'}</td>
                  <td>{v.email || '-'}</td>
                  <td>{v.modalities?.length ? v.modalities.map((modality) => modality.code).join(', ') : '-'}</td>
                  <td>{v.lifecycle_state}</td>
                  <td className={styles.actions}>
                    <Link className={styles.actionLink} to={`/vendors/${v.id}`}>View</Link>
                    <button onClick={() => startEdit(v)}>Edit</button>
                  </td>
                </tr>
              ))}
              {!loading && vendors.length === 0 ? (
                <tr>
                  <td colSpan={6}>No vendors found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      {editId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Edit Vendor</h2>
              <button type="button" className={styles.secondaryButton} onClick={cancelEdit}>Close</button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleUpdate} className={styles.grid3}>
              <input required placeholder="Vendor code (e.g AAA01)" value={editForm.vendor_alpha_code} onChange={(e) => setEditForm((v) => ({ ...v, vendor_alpha_code: e.target.value.toUpperCase() }))} />
              <input required placeholder="Vendor name" value={editForm.name} onChange={(e) => setEditForm((v) => ({ ...v, name: e.target.value }))} />
              <input placeholder="Country" value={editForm.country} onChange={(e) => setEditForm((v) => ({ ...v, country: e.target.value.toUpperCase() }))} maxLength={2} />
              <input type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm((v) => ({ ...v, email: e.target.value }))} />
              <input placeholder="Phone" value={editForm.phone} onChange={(e) => setEditForm((v) => ({ ...v, phone: e.target.value }))} />
              <input placeholder="Website" value={editForm.website} onChange={(e) => setEditForm((v) => ({ ...v, website: e.target.value }))} />
              <label className={styles.checkboxField}>
                <input type="checkbox" checked={editForm.is_vendor} onChange={(e) => setEditForm((v) => ({ ...v, is_vendor: e.target.checked }))} />
                <span>Is vendor</span>
              </label>
              <select value={editForm.lifecycle_state} onChange={(e) => setEditForm((v) => ({ ...v, lifecycle_state: e.target.value }))}>
                <option value="active">active</option>
                <option value="disabled">disabled</option>
                <option value="retired">retired</option>
              </select>
              <select
                multiple
                className={styles.multiSelect}
                value={editForm.modality_ids}
                onChange={(e) => setEditForm((v) => ({ ...v, modality_ids: getSelectedValues(e) }))}
              >
                {modalities.map((modality) => (
                  <option key={modality.id} value={modality.id}>
                    {modality.code} - {modality.name}
                  </option>
                ))}
              </select>
              <input className={styles.colSpan3} placeholder="Address" value={editForm.address} onChange={(e) => setEditForm((v) => ({ ...v, address: e.target.value }))} />
              <textarea className={styles.colSpan3} placeholder="Description" value={editForm.description} onChange={(e) => setEditForm((v) => ({ ...v, description: e.target.value }))} />
              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Vendor'}</button>
                <button type="button" className={styles.secondaryButton} onClick={cancelEdit}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VendorsPage;
